/* hbge-ft-click build:2026-08-09Tlive */
/**
 * Homebridge Growth Engine — first-touch advertising ID capture (browser).
 *
 * - Stores Google (gclid/wbraid/gbraid) and Meta (fbclid/fbc/fbp) identifiers
 *   in versioned localStorage only (never HBGE click-ID cookies).
 * - Reads Meta `_fbc` / `_fbp` cookies when format-valid; never invents fbc
 *   from fbclid.
 * - Fill-only enrichment: if first-touch stored fbclid before cookies existed,
 *   later passes may add matching `_fbc` / `_fbp` without changing fbclid.
 * - Bounded Meta cookie enrichment polling after consent allow (≈250 ms ticks,
 *   hard 20 s ceiling; stop early once missing fbc/fbp are safely filled).
 * - Consent revoke clears deferred schedule eligibility; same-document re-grant
 *   reschedules once (generation-guarded; no duplicate cohorts while allowed).
 * - Per-type first-touch; each type expires 90 days from its own capturedAt.
 * - Forwards onto LeadConnector booking iframe src as hbge_* query keys only.
 * - Does not rewrite the visible page URL; does not log identifiers.
 * - Gates capture/forward on Shopify Customer Privacy marketingAllowed().
 * - Responds to consent changes after load (allow → capture; deny → clear/strip).
 * - Observes dynamically inserted booking iframes.
 *
 * Keep logic aligned with app/integrations/attribution/first-touch-click-capture.ts
 */
(function hbgeFirstTouchClickCapture() {
  "use strict";

  var STORAGE_KEY = "hbge_ft_click_v1";
  var STORAGE_VERSION = 1;
  var TTL_MS = 90 * 24 * 60 * 60 * 1000;
  var MAX_CHARS = 256;
  var TYPES = ["gclid", "wbraid", "gbraid", "fbclid", "fbc", "fbp"];
  var IFRAME_KEYS = {
    gclid: "hbge_gclid",
    wbraid: "hbge_wbraid",
    gbraid: "hbge_gbraid",
    fbclid: "hbge_fbclid",
    fbc: "hbge_fbc",
    fbp: "hbge_fbp",
  };
  var META_COOKIE_NAMES = { fbc: "_fbc", fbp: "_fbp" };
  var LEGACY_COOKIE = "hbge_ft_click";
  var META_COOKIE_SHAPE = /^fb\.\d+\.\d+\./;
  var OBSERVER_FLAG = "__hbgeFtClickObserver";
  var COOKIE_ENRICH_POLL_INTERVAL_MS = 250;
  var COOKIE_ENRICH_CEILING_MS = 20000;
  // In-document schedule cohort. Deny bumps generation so pending polls no-op;
  // re-grant may arm a new bounded poll cohort without duplicates while allowed.
  var enrichSchedule = { scheduled: false, generation: 0 };

  function hasControlChars(value) {
    for (var i = 0; i < value.length; i++) {
      var code = value.charCodeAt(i);
      if (code <= 0x1f || code === 0x7f) return true;
    }
    return false;
  }

  function isPlaceholder(trimmed) {
    var lower = trimmed.toLowerCase();
    if (lower === "null" || lower === "undefined") return true;
    if (trimmed.indexOf("{{") !== -1 || trimmed.indexOf("}}") !== -1) {
      return true;
    }
    return false;
  }

  function normalize(raw) {
    if (typeof raw !== "string") return null;
    if (hasControlChars(raw)) return null;
    var trimmed = raw.trim();
    if (!trimmed || trimmed.length > MAX_CHARS) return null;
    if (hasControlChars(trimmed)) return null;
    if (isPlaceholder(trimmed)) return null;
    return trimmed;
  }

  function normalizeMetaCookie(raw) {
    var value = normalize(raw);
    if (!value || !META_COOKIE_SHAPE.test(value)) return null;
    return value;
  }


  function extractFbclidFromFbc(fbc) {
    var value = normalizeMetaCookie(fbc);
    if (!value) return null;
    var match = /^fb\.\d+\.\d+\.(.+)$/.exec(value);
    if (!match || !match[1]) return null;
    return normalize(match[1]);
  }

  function isSafeMetaCookieEnrichment(storedFbclid, cookieType, cookieValue) {
    var cookie = normalizeMetaCookie(cookieValue);
    if (!cookie) return false;
    if (cookieType === "fbp") return true;
    var stored = normalize(storedFbclid);
    if (!stored) return true;
    var embedded = extractFbclidFromFbc(cookie);
    return Boolean(embedded && embedded === stored);
  }

  function filterSafeCookies(storedFlat, cookies) {
    var out = {};
    if (
      cookies.fbc &&
      isSafeMetaCookieEnrichment(storedFlat.fbclid, "fbc", cookies.fbc)
    ) {
      out.fbc = normalizeMetaCookie(cookies.fbc);
    }
    if (
      cookies.fbp &&
      isSafeMetaCookieEnrichment(storedFlat.fbclid, "fbp", cookies.fbp)
    ) {
      out.fbp = normalizeMetaCookie(cookies.fbp);
    }
    return out;
  }

  function composeForwardIds(storedFlat, liveCookies) {
    var safe = filterSafeCookies(storedFlat, liveCookies || {});
    var out = {};
    for (var k in storedFlat) {
      if (Object.prototype.hasOwnProperty.call(storedFlat, k)) {
        out[k] = storedFlat[k];
      }
    }
    if (!out.fbc && safe.fbc) out.fbc = safe.fbc;
    if (!out.fbp && safe.fbp) out.fbp = safe.fbp;
    return out;
  }


  function clearLegacyCookie() {
    try {
      document.cookie =
        LEGACY_COOKIE +
        "=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure";
    } catch (_e) {
      /* ignore */
    }
  }

  function getLocalStorage() {
    try {
      var ls = window.localStorage;
      if (!ls) return null;
      var probe = "__hbge_ft_probe__";
      ls.setItem(probe, "1");
      ls.removeItem(probe);
      return ls;
    } catch (_e) {
      return null;
    }
  }

  function resolveConsent() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api) return "unknown";
      if (typeof api.marketingAllowed === "function" && api.marketingAllowed()) {
        return "allowed";
      }
      if (typeof api.currentVisitorConsent === "function") {
        var consent = api.currentVisitorConsent();
        if (consent && consent.marketing === "yes") return "allowed";
        if (consent && consent.marketing === "no") return "denied";
      }
      if (typeof api.marketingAllowed === "function") {
        return api.marketingAllowed() ? "allowed" : "denied";
      }
    } catch (_e) {
      return "unknown";
    }
    return "unknown";
  }

  function resolveConsentFromEventDetail(detail) {
    if (detail && typeof detail.marketingAllowed === "boolean") {
      return detail.marketingAllowed ? "allowed" : "denied";
    }
    return resolveConsent();
  }

  function parseCapturedAtMs(capturedAt) {
    var ms = Date.parse(capturedAt);
    return Number.isFinite(ms) ? ms : null;
  }

  function isExpired(entry, nowMs) {
    var capturedMs = parseCapturedAtMs(entry.capturedAt);
    if (capturedMs == null || nowMs < capturedMs) return true;
    return nowMs - capturedMs > TTL_MS;
  }

  function sanitizePayload(raw, nowMs) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    if (raw.version !== STORAGE_VERSION) return null;
    var idsRaw =
      raw.ids && typeof raw.ids === "object" && !Array.isArray(raw.ids)
        ? raw.ids
        : {};
    var ids = {};
    for (var i = 0; i < TYPES.length; i++) {
      var type = TYPES[i];
      var entryRaw = idsRaw[type];
      if (!entryRaw || typeof entryRaw !== "object" || Array.isArray(entryRaw)) {
        continue;
      }
      var value =
        type === "fbc" || type === "fbp"
          ? normalizeMetaCookie(entryRaw.value)
          : normalize(entryRaw.value);
      if (!value || typeof entryRaw.capturedAt !== "string") continue;
      var entry = { value: value, capturedAt: entryRaw.capturedAt };
      if (isExpired(entry, nowMs)) continue;
      ids[type] = entry;
    }
    return { version: STORAGE_VERSION, ids: ids };
  }

  function serializePayload(payload) {
    var ids = {};
    for (var i = 0; i < TYPES.length; i++) {
      var type = TYPES[i];
      var entry = payload.ids[type];
      if (!entry) continue;
      var value =
        type === "fbc" || type === "fbp"
          ? normalizeMetaCookie(entry.value)
          : normalize(entry.value);
      if (!value || typeof entry.capturedAt !== "string") continue;
      ids[type] = { value: value, capturedAt: entry.capturedAt };
    }
    return JSON.stringify({ version: STORAGE_VERSION, ids: ids });
  }

  function readStorage(ls, nowMs) {
    if (!ls) return { payload: null, unavailable: true };
    var raw;
    try {
      raw = ls.getItem(STORAGE_KEY);
    } catch (_e) {
      return { payload: null, unavailable: true };
    }
    if (raw == null || String(raw).trim() === "") {
      return { payload: null, unavailable: false, raw: null };
    }
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (_e2) {
      return { payload: null, unavailable: false, raw: raw, malformed: true };
    }
    var payload = sanitizePayload(parsed, nowMs);
    if (!payload) {
      return { payload: null, unavailable: false, raw: raw, malformed: true };
    }
    var rewritten = serializePayload(payload);
    return {
      payload: payload,
      unavailable: false,
      raw: raw,
      shouldRewrite: rewritten !== raw,
    };
  }

  function writeStorage(ls, payload) {
    if (!ls) return false;
    try {
      ls.setItem(STORAGE_KEY, serializePayload(payload));
      return true;
    } catch (_e) {
      return false;
    }
  }

  function clearStorage(ls) {
    if (!ls) return;
    try {
      ls.removeItem(STORAGE_KEY);
    } catch (_e) {
      /* ignore */
    }
  }

  function extractFromSearch(search) {
    var params = new URLSearchParams(
      search.charAt(0) === "?" ? search.slice(1) : search,
    );
    var out = {};
    for (var i = 0; i < TYPES.length; i++) {
      var type = TYPES[i];
      var native = normalize(params.get(type));
      var hbge = normalize(params.get(IFRAME_KEYS[type]));
      var value = native || hbge;
      if ((type === "fbc" || type === "fbp") && value) {
        value = normalizeMetaCookie(value);
      }
      if (value) out[type] = value;
    }
    return out;
  }

  function extractMetaCookies(cookieHeader) {
    var out = {};
    if (!cookieHeader || String(cookieHeader).trim() === "") return out;
    var parts = String(cookieHeader).split(";");
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      var eq = part.indexOf("=");
      if (eq <= 0) continue;
      var name = part.slice(0, eq).trim();
      var rawValue = part.slice(eq + 1).trim();
      if (name === META_COOKIE_NAMES.fbc) {
        var fbc = normalizeMetaCookie(rawValue);
        if (fbc) out.fbc = fbc;
      } else if (name === META_COOKIE_NAMES.fbp) {
        var fbp = normalizeMetaCookie(rawValue);
        if (fbp) out.fbp = fbp;
      }
    }
    return out;
  }

  function mergeFirstTouch(stored, incoming, nowIso, nowMs) {
    var base = stored
      ? sanitizePayload(stored, nowMs) || { version: STORAGE_VERSION, ids: {} }
      : { version: STORAGE_VERSION, ids: {} };
    var nextIds = {};
    for (var k in base.ids) {
      if (Object.prototype.hasOwnProperty.call(base.ids, k)) {
        nextIds[k] = base.ids[k];
      }
    }
    for (var i = 0; i < TYPES.length; i++) {
      var type = TYPES[i];
      var existing = nextIds[type];
      if (existing && !isExpired(existing, nowMs)) {
        nextIds[type] = {
          value: existing.value,
          capturedAt: existing.capturedAt,
        };
        continue;
      }
      delete nextIds[type];
      var candidate =
        type === "fbc" || type === "fbp"
          ? normalizeMetaCookie(incoming[type])
          : normalize(incoming[type]);
      if (candidate) {
        nextIds[type] = { value: candidate, capturedAt: nowIso };
      }
    }
    return { version: STORAGE_VERSION, ids: nextIds };
  }

  function flatIds(payload) {
    var out = {};
    if (!payload || !payload.ids) return out;
    for (var i = 0; i < TYPES.length; i++) {
      var type = TYPES[i];
      if (payload.ids[type]) out[type] = payload.ids[type].value;
    }
    return out;
  }

  function isBookingIframeSrc(src) {
    try {
      var url = new URL(src, window.location.href);
      return (
        url.hostname === "api.leadconnectorhq.com" &&
        url.pathname.indexOf("/widget/booking/") === 0
      );
    } catch (_e) {
      return false;
    }
  }

  function applyIdsToIframeUrl(href, ids) {
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (_e) {
      return href;
    }
    var changed = false;
    for (var i = 0; i < TYPES.length; i++) {
      var type = TYPES[i];
      var value =
        type === "fbc" || type === "fbp"
          ? normalizeMetaCookie(ids[type])
          : normalize(ids[type]);
      if (!value) continue;
      var key = IFRAME_KEYS[type];
      var existingHbge = normalize(url.searchParams.get(key));
      var existingNative = normalize(url.searchParams.get(type));
      if (existingHbge || existingNative) continue;
      url.searchParams.set(key, value);
      changed = true;
    }
    return changed ? url.toString() : href;
  }

  function stripIdsFromIframeUrl(href) {
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (_e) {
      return href;
    }
    var changed = false;
    for (var i = 0; i < TYPES.length; i++) {
      var key = IFRAME_KEYS[TYPES[i]];
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    return changed ? url.toString() : href;
  }

  function stripIframeParams() {
    var frames = document.querySelectorAll("iframe[src]");
    for (var i = 0; i < frames.length; i++) {
      var frame = frames[i];
      var src = frame.getAttribute("src");
      if (!src || !isBookingIframeSrc(src)) continue;
      var next = stripIdsFromIframeUrl(src);
      if (next !== src) {
        frame.setAttribute("src", next);
      }
    }
  }

  function forwardToIframes(ids) {
    var frames = document.querySelectorAll("iframe[src]");
    for (var i = 0; i < frames.length; i++) {
      var frame = frames[i];
      var src = frame.getAttribute("src");
      if (!src || !isBookingIframeSrc(src)) continue;
      var next = applyIdsToIframeUrl(src, ids);
      if (next !== src) {
        frame.setAttribute("src", next);
      }
    }
  }

  function hasAnyId(ids) {
    for (var i = 0; i < TYPES.length; i++) {
      if (ids[TYPES[i]]) return true;
    }
    return false;
  }

  function runWithConsent(consent) {
    clearLegacyCookie();

    var ls = getLocalStorage();

    if (consent === "denied") {
      // Reset deferred-enrichment eligibility for a later same-document re-grant.
      enrichSchedule = {
        scheduled: false,
        generation: enrichSchedule.generation + 1,
      };
      clearStorage(ls);
      stripIframeParams();
      return;
    }

    if (consent !== "allowed") {
      // unknown: fail closed — do not capture, forward, or clear.
      return;
    }

    if (!ls) {
      return;
    }

    var nowMs = Date.now();
    var nowIso = new Date(nowMs).toISOString();
    var read = readStorage(ls, nowMs);

    if (read.unavailable) {
      return;
    }

    if (read.malformed) {
      clearStorage(ls);
      read = { payload: null, unavailable: false };
    } else if (read.shouldRewrite && read.payload) {
      writeStorage(ls, read.payload);
    }

    // Capture from current location.search so allow-after-load works without reload
    // while landing-page click params are still present. Then fill-only enrich
    // missing fbc/fbp from Meta cookies when safe for the first-touch fbclid.
    // fbc is never invented from fbclid.
    var fromSearchRaw = extractFromSearch(window.location.search || "");
    var existingFlat = flatIds(read.payload);
    var anchorFbclid = existingFlat.fbclid || fromSearchRaw.fbclid;
    var fromSearch = {};
    for (var sk in fromSearchRaw) {
      if (Object.prototype.hasOwnProperty.call(fromSearchRaw, sk)) {
        fromSearch[sk] = fromSearchRaw[sk];
      }
    }
    if (
      fromSearch.fbc &&
      !isSafeMetaCookieEnrichment(anchorFbclid, "fbc", fromSearch.fbc)
    ) {
      delete fromSearch.fbc;
    }
    var afterSearch = mergeFirstTouch(read.payload, fromSearch, nowIso, nowMs);
    var afterSearchFlat = flatIds(afterSearch);
    var cookies = extractMetaCookies(document.cookie || "");
    var safeCookies = filterSafeCookies(afterSearchFlat, cookies);
    var cookieIncoming = {};
    if (!afterSearchFlat.fbc && safeCookies.fbc) cookieIncoming.fbc = safeCookies.fbc;
    if (!afterSearchFlat.fbp && safeCookies.fbp) cookieIncoming.fbp = safeCookies.fbp;
    var merged = mergeFirstTouch(afterSearch, cookieIncoming, nowIso, nowMs);
    var storedFlat = flatIds(merged);
    var ids = composeForwardIds(storedFlat, cookies);

    if (!hasAnyId(ids)) {
      if (read.raw) clearStorage(ls);
      return;
    }

    var before = read.payload ? serializePayload(read.payload) : "";
    var after = serializePayload(merged);
    if (before !== after) {
      writeStorage(ls, merged);
    }

    forwardToIframes(ids);
  }

  function runCaptureAndForward() {
    runWithConsent(resolveConsent());
  }

  function metaCookieFieldsStillMissing(ids) {
    return !ids.fbc || !ids.fbp;
  }

  function scheduleCookieEnrichment() {
    if (enrichSchedule.scheduled) return;
    enrichSchedule.scheduled = true;
    var armedGeneration = enrichSchedule.generation;
    var startedAt = Date.now();

    function tick() {
      if (!enrichSchedule.scheduled) return;
      if (armedGeneration !== enrichSchedule.generation) return;
      if (resolveConsent() !== "allowed") return;

      var elapsed = Date.now() - startedAt;
      if (elapsed > COOKIE_ENRICH_CEILING_MS) return;

      runWithConsent("allowed");

      var ls = getLocalStorage();
      if (!ls) return;
      var read = readStorage(ls, Date.now());
      var flat = flatIds(read.payload);
      if (!metaCookieFieldsStillMissing(flat)) return;

      elapsed = Date.now() - startedAt;
      if (elapsed >= COOKIE_ENRICH_CEILING_MS) return;

      var remaining = COOKIE_ENRICH_CEILING_MS - elapsed;
      var delay = Math.min(COOKIE_ENRICH_POLL_INTERVAL_MS, remaining);
      if (delay > 0) {
        setTimeout(tick, delay);
      }
    }

    // Immediate first enrichment tick (same document; no reload/navigation).
    tick();
  }

  /**
   * Consent transition entry: immediate capture + at-most-one bounded poll
   * cohort while allowed; deny clears schedule so re-grant can reschedule.
   */
  function handleConsentTransition(consent) {
    if (consent === "denied") {
      runWithConsent("denied");
      return;
    }
    if (consent !== "allowed") {
      runWithConsent(consent);
      return;
    }
    runWithConsent("allowed");
    scheduleCookieEnrichment();
  }


  function observeDynamicIframes() {
    if (document[OBSERVER_FLAG] || typeof MutationObserver !== "function") {
      return;
    }
    document[OBSERVER_FLAG] = true;
    var observer = new MutationObserver(function (mutations) {
      var found = false;
      for (var i = 0; i < mutations.length && !found; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (!node || node.nodeType !== 1) continue;
          if (node.tagName === "IFRAME") {
            found = true;
            break;
          }
          if (typeof node.querySelector === "function" && node.querySelector("iframe")) {
            found = true;
            break;
          }
        }
      }
      if (found) runCaptureAndForward();
    });
    var root = document.documentElement || document.body;
    if (root) {
      observer.observe(root, { childList: true, subtree: true });
    }
  }

  function onConsentReady() {
    handleConsentTransition(resolveConsent());
    observeDynamicIframes();
  }

  function loadCustomerPrivacy(callback) {
    var shopify = window.Shopify;
    if (
      shopify &&
      shopify.customerPrivacy &&
      typeof shopify.customerPrivacy.marketingAllowed === "function"
    ) {
      callback();
      return;
    }
    if (!shopify || typeof shopify.loadFeatures !== "function") {
      return;
    }
    shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (error) {
        if (error) return;
        callback();
      },
    );
  }

  function bindConsentListener() {
    try {
      // Shopify publishes visitorConsentCollected whenever consent changes
      // (allow or deny), not only on first collection.
      document.addEventListener("visitorConsentCollected", function (event) {
        var detail = event && event.detail;
        handleConsentTransition(resolveConsentFromEventDetail(detail));
      });
    } catch (_e) {
      /* ignore */
    }
    try {
      // Positive-only signal; re-check API so allow-after-load still captures
      // current location.search without a reload.
      document.addEventListener("trackingConsentAccepted", function () {
        handleConsentTransition(resolveConsent());
      });
    } catch (_e2) {
      /* ignore */
    }
  }

  function boot() {
    clearLegacyCookie();
    bindConsentListener();
    loadCustomerPrivacy(onConsentReady);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
